import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const BG = "https://images.unsplash.com/photo-1734630341082-0fec0e10126c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxneW0lMjBiYXJiZWxsJTIwd2VpZ2h0cyUyMGRhcmt8ZW58MHx8fHwxNzg1NzY2MTM4fDA&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Welcome back." : "Account created.");
      navigate(mode === "login" ? "/dashboard" : "/plans");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A] text-white relative z-10">
      <div className="relative hidden lg:block">
        <img src={BG} alt="gym" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/50" />
        <div className="absolute bottom-12 left-12 max-w-sm">
          <h2 className="font-display font-black text-5xl uppercase tracking-tighter leading-none">Discipline<br />is a <span className="text-[#FF3B30]">choice.</span></h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Flame className="text-[#FF3B30]" size={26} strokeWidth={2.5} />
            <span className="font-display font-black text-2xl uppercase tracking-tighter">Reprise<span className="text-[#FF3B30]">Fit</span></span>
          </Link>

          <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-2">
            {mode === "login" ? "Log in" : "Create account"}
          </h1>
          <p className="text-white/50 mb-8 text-sm">
            {mode === "login" ? "Get back to the grind." : "Start tracking in 30 seconds."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Name</label>
                <input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-2 w-full bg-[#121212] border border-white/10 px-4 py-3 focus:border-[#FF3B30] focus:outline-none transition-colors" />
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Email</label>
              <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-2 w-full bg-[#121212] border border-white/10 px-4 py-3 focus:border-[#FF3B30] focus:outline-none transition-colors" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Password</label>
              <input data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-2 w-full bg-[#121212] border border-white/10 px-4 py-3 focus:border-[#FF3B30] focus:outline-none transition-colors" />
            </div>
            <button data-testid="auth-submit" disabled={busy} type="submit"
              className="w-full bg-[#FF3B30] hover:bg-[#FF453A] disabled:opacity-50 text-white font-bold uppercase tracking-wide px-6 py-3.5 flex items-center justify-center gap-2 transition-transform duration-150 hover:-translate-y-0.5 shadow-[0_0_15px_rgba(255,59,48,0.3)]">
              {busy ? "Please wait..." : mode === "login" ? "Log in" : "Sign up"} <ArrowRight size={18} />
            </button>
          </form>

          <div className="mt-6 text-sm text-white/50">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#FF3B30] font-semibold hover:underline">
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
